import { useTradeLogisticsBlocks } from 'views/Conversation/Sider/useTradeLogisticsBlocks';
import { LogisticsInfo } from '../LogisticsInfo';
import { formatLogisticsInfo } from '../LogisticsInfo/utils';
import { useTranslate } from '@tolgee/react';
import { DetailCard } from '../DetailCard';
import { useOrderStatusLabel } from 'hooks/business/chat/useOrder';
import { CommentCard } from './CommentCard';
import { OrderQueryParams } from 'hooks/business/chat/useOrderList';
import { PayCard } from './PayCard';
import { OrderProductList } from '../OrderCard/OrderProductList';
import { Customer } from './Customer';
import { ERPDetail } from './ERPDetail';
import { InfoBox } from './InfoBox';
import { useMemo } from 'react';
import { useIsYPHH } from 'yphh/feature';
import { OrderDrawerDetailBody } from '../OrderDrawer/DetailBody';
import { InvoiceButton } from '../OrderDrawer/InvoiceButton';
import {
  getTradeDetailBlocks,
  useTradeDetailBasicInfoBlocks,
} from 'views/Conversation/Sider/useTradeDetailBusiness';
import { isOrderCompleted } from 'components/LightConversation/utils/order';

interface OrderDetailProps {
  conversation: Conversation.Conversation;
  order: Conversation.Trade.Vo;
  sendable: boolean;
  /** 用于乐观更新的查询参数 */
  queryParams?: OrderQueryParams;
}

export const OrderDetail = (props: OrderDetailProps) => {
  const { conversation, order, sendable, queryParams } = props;
  const { t } = useTranslate();
  const { tradeStatus, taxIdType, taxIdNumber, bookingId, erpDetail, items } = order;
  const { logisticsInfo, latestLogisticsTime } = useTradeLogisticsBlocks(order, { sendable });
  const orderStatusLabel = useOrderStatusLabel(tradeStatus);
  const isYPHH = useIsYPHH();

  const basicInfoBlocks = useTradeDetailBasicInfoBlocks(order);

  const baseInfoList = useMemo(() => {
    const blocks = getTradeDetailBlocks(tradeStatus, basicInfoBlocks);
    const fromBlocks = blocks.map((block) => ({
      title: block.type === 'text' ? block.label : t(block.id),
      key: block.id as string,
      value: block.type === 'text' ? block.value : undefined,
    }));
    const isCompleted = isOrderCompleted(tradeStatus);

    return [
      ...fromBlocks,
      {
        title: t('trade.latestLogisticsTime'),
        key: 'latestLogisticsTime',
        value: isCompleted ? undefined : latestLogisticsTime?.value,
      },
      {
        title: taxIdType,
        key: 'taxIdNumber',
        value: taxIdNumber,
      },
      {
        title: 'BookingID',
        key: 'bookingId',
        value: bookingId,
      },
    ];
  }, [
    tradeStatus,
    basicInfoBlocks,
    t,
    latestLogisticsTime?.value,
    taxIdType,
    taxIdNumber,
    bookingId,
  ]);

  const logisticsList = useMemo(() => {
    if (!logisticsInfo) return [];
    return formatLogisticsInfo(logisticsInfo.payload);
  }, [logisticsInfo]);

  return (
    <OrderDrawerDetailBody
      footer={
        <div className="flex justify-start">
          <InvoiceButton order={order} conversation={conversation} />
        </div>
      }
    >
      <div className="w-full grid grid-cols-[1fr_338px] gap-4">
        <div className="flex flex-col gap-4">
          <DetailCard title={orderStatusLabel}>
            <InfoBox list={baseInfoList} />
          </DetailCard>
          {isYPHH && !!erpDetail?.erpTradeStatus && <ERPDetail erpDetail={erpDetail} />}
          {logisticsList.length > 0 && (
            <DetailCard title={t('trade.物流')}>
              <LogisticsInfo
                conversation={conversation}
                list={logisticsList}
                sendable={sendable}
                showAll={false}
                cols={3}
              />
            </DetailCard>
          )}
          <DetailCard title={t('x个商品', { count: items.length })}>
            <div className="flex flex-col gap-3 bg-[#F7F9FA] rounded px-3">
              <OrderProductList
                order={order}
                products={items}
                defaultShowCount={2}
                simple={false}
                showTotalAmount={false}
                showComment
              />
            </div>
          </DetailCard>
          <CommentCard order={order} queryParams={queryParams} />
        </div>
        <div className="flex flex-col gap-4">
          <PayCard order={order} />
          <Customer order={order} />
        </div>
      </div>
    </OrderDrawerDetailBody>
  );
};
